import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button, Callout, Divider } from '../../../../components';
import { ROUTE } from '../../../../routes';
import { useFeatureGate } from '../../../../hooks/useFeatureGate';
import { assetKindOpens } from '../../../creatives/components/AssetComposerOverlay';
import { campaignsLandingOn, playersFromLandingPagePath } from '../../../creatives/link-attribution';
import { openInApp } from '../../../creatives/components/LandingPageComposer/link-for';
import { useBrandLinks } from '../../../creatives/useBrandLinks';
import { useCampaignStats, type CampaignStats } from '../../../creatives/useCampaignStats';
import { VIDEO_STAT_DEFS, statRatio, videoStatValue } from '../../../creatives/video-stats';
import { KpiTile, type KpiFigure } from '../KpiTile';
import { GlyphBadge } from '../ShareTiles';
import type { StatsDelta } from '../../../../mock-data/types';
import { periodDelta } from '../../format';
import type { Span } from '../../queries';
import { Tiles } from '../../website-analytics.styles';
import { Block, Gap, Head, HeadMark, HeadText, Title, Sub, PlayersLink } from './PageConversions.styles';

export type PageConversionsProps = {
  /** The Studio landing page's spark id; absent for a live page Studio did not make (a link can still point at it by
   *  its address, but its sign-ups are only counted by this id, so they cannot be shown). */
  landingPageId?: string;
  /** The brand's landing pages are still being read (the page's Studio id may not be known yet). */
  resolving?: boolean;
  /** The brand's landing pages could not be read, so whether a link leads to the page is not known. */
  resolveFailed?: boolean;
  current: Span;
  previous: Span;
  /** "vs the 30 days before" (already translated). */
  caption: string;
};

/** A UTC day ("2026-10-12"): the analytics periods are whole UTC days, and the stats range names days. */
const utcDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** The stats range of an analytics span: its first and last day (the span's end is exclusive). */
const daysOf = (span: Span): { from: string; to: string } => ({
  from: utcDay(span.from),
  to: utcDay(Math.max(span.from, span.to - 1)),
});

const statOf = (stats: CampaignStats, id: string): number | null => {
  const def = VIDEO_STAT_DEFS.find((d) => d.id === id);
  return stats.status === 'ok' && def ? videoStatValue(stats.metricsList, def) : null;
};

const changeOf = (current: number | null, previous: number | null): StatsDelta | null =>
  current === null || previous === null ? null : periodDelta(current, previous);

const money = (value: number | null): KpiFigure | null => (value === null ? null : { value, prefix: '$' });
const count = (value: number | null): KpiFigure | null => (value === null ? null : { value });

/**
 * Stats → Website, one landing page picked: what it brought in over the period, from the Owners Hub stats the
 * brand's tracking links feed (`daily_affiliate_activity` by campaign), summed over every link landing on the page
 * (its own link included), as the page's own stats sheet counts them. Only visitors who came through one of those
 * links are counted, so these do not add up with the visitors above. Compared with the period before, like the
 * traffic figures.
 */
export const PageConversions = ({
  landingPageId,
  resolving = false,
  resolveFailed = false,
  current,
  previous,
  caption,
}: PageConversionsProps): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const gate = useFeatureGate();
  // The way to Marketing only where its landing pages open for this owner (both of their route's gates are on).
  const marketingOpens = assetKindOpens('landing_page', gate.isEnabled);
  const brand = useBrandLinks();
  const settled = !brand.loading;
  const campaigns = React.useMemo(
    () => (settled && landingPageId ? [...new Set(campaignsLandingOn(brand.links, landingPageId))] : []),
    [settled, brand.links, landingPageId],
  );
  const now = useCampaignStats({ campaigns, range: daysOf(current) });
  const before = useCampaignStats({ campaigns, range: daysOf(previous) });

  const linksFailed = !!brand.error && brand.links.length === 0;
  // Without the brand's landing pages the page's Studio id is unknown, not absent: no "no tracking link" claim then.
  const pageFailed = !landingPageId && !resolving && resolveFailed;
  const unknownPage = !landingPageId && !resolving && !resolveFailed;
  const noLinks = !!landingPageId && settled && !linksFailed && campaigns.length === 0;
  // With the links unread there is nothing to ask the stats for ('none'), which is a failure here, not a wait.
  const loading =
    !noLinks &&
    !unknownPage &&
    (resolving || brand.loading || now.status === 'loading' || (now.status === 'none' && !linksFailed));
  const error = linksFailed || now.status === 'error';

  const regs = statOf(now, 'registrations');
  const ftds = statOf(now, 'ftds');
  const ftdShare = regs !== null && ftds !== null ? statRatio(ftds, regs) : undefined;
  const playersPath = landingPageId ? playersFromLandingPagePath(landingPageId) : '';

  return (
    <Block>
      <Gap>
        <Divider variant="styled" />
      </Gap>
      <Head>
        <HeadMark>
          <GlyphBadge name="bill" />
          <HeadText>
            <Title text="Sign-ups from this page" tag="h3" />
            <Sub text="Counted from the tracking links that lead to it" tag="span" />
          </HeadText>
        </HeadMark>
        {playersPath && regs !== null && regs > 0 ? (
          <PlayersLink href={playersPath} onClick={(e) => openInApp(e, playersPath, (path) => navigate(path))}>
            {t('See players')}
          </PlayersLink>
        ) : null}
      </Head>
      {pageFailed ? (
        <Callout
          tone="danger"
          title={t('Not available right now')}
          text="We could not load your pages. Try again in a moment."
        />
      ) : unknownPage ? (
        <Callout
          tone="info"
          title={t("Sign-ups for this page can't be shown")}
          text="This page wasn't made in Studio, so the sign-ups its tracking links bring in can't be counted here yet."
        />
      ) : noLinks && marketingOpens ? (
        <Callout
          tone="warning"
          title={t('No tracking link leads to this page yet')}
          text="Sign-ups are counted from tracking links, so this page's cannot be shown. Get its link in Marketing."
          action={
            <Button
              variant="outline"
              label={t('Open in Marketing')}
              onClick={() =>
                navigate(landingPageId ? ROUTE.creativesLandingPage(landingPageId) : ROUTE.creativesLandingPages())
              }
            />
          }
        />
      ) : noLinks ? (
        <Callout
          tone="warning"
          title={t('No tracking link leads to this page yet')}
          text="Sign-ups are counted from tracking links, so this page's cannot be shown yet."
        />
      ) : (
        <Tiles>
          <KpiTile
            label={t('Registrations')}
            hint={t('People who signed up after coming through a link')}
            figure={count(regs)}
            change={changeOf(regs, statOf(before, 'registrations'))}
            caption={caption}
            loading={loading}
            error={error}
            sunken
          />
          <KpiTile
            label={t('FTDs')}
            hint={
              ftdShare !== undefined
                ? t('{{pct}}% of them made a first deposit', { pct: Math.round(ftdShare * 100) })
                : t('People who made their first deposit')
            }
            figure={count(ftds)}
            change={changeOf(ftds, statOf(before, 'ftds'))}
            caption={caption}
            loading={loading}
            error={error}
            sunken
          />
          <KpiTile
            label={t('Deposit volume')}
            hint={t('Everything they deposited')}
            figure={money(statOf(now, 'deposit_amount'))}
            change={changeOf(statOf(now, 'deposit_amount'), statOf(before, 'deposit_amount'))}
            caption={caption}
            loading={loading}
            error={error}
            sunken
          />
          <KpiTile
            label={t('GGR')}
            hint={t('What the casino won from their play')}
            figure={money(statOf(now, 'ggr'))}
            change={changeOf(statOf(now, 'ggr'), statOf(before, 'ggr'))}
            caption={caption}
            loading={loading}
            error={error}
            sunken
          />
        </Tiles>
      )}
    </Block>
  );
};

export default PageConversions;
